import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { setTokens } from '@/lib/tokenStore';
import { Loader2, AlertTriangle } from 'lucide-react';

/**
 * /auth/callback — Identity Platform redirect target.
 *
 * Stores tokens from URL into in-memory tokenStore. AuthProvider's effect on
 * `[token]` will pick this up and fetch /me, populating `user`. We then
 * OBSERVE `user` (not blindly navigate after setTokens) — that observation is
 * what avoids the redirect-loop race condition where navigate() fires before
 * React commits the new user state, causing ProtectedRoute to see stale null.
 *
 * Tokens are wiped from URL via history.replaceState (security — no leak via
 * referer or sessionStorage). Tokens NEVER hit localStorage (memory-only spec).
 */
const AuthCallback = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [error, setError] = useState(null);
  const [tokensApplied, setTokensApplied] = useState(false);

  // 1) Extract tokens from URL → store in memory. Once.
  useEffect(() => {
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    if (!accessToken) {
      setError('Geen tokens ontvangen van Identity Platform.');
      return;
    }
    setTokens(accessToken, refreshToken);
    setTokensApplied(true);
  }, [params]);

  // 2) Once user state is committed by AuthProvider → navigate to dashboard.
  useEffect(() => {
    if (tokensApplied && user) {
      // Wipe tokens from URL bar so a refresh doesn't re-trigger this flow.
      window.history.replaceState({}, document.title, '/auth/callback');
      navigate('/', { replace: true });
    }
  }, [tokensApplied, user, navigate]);

  // 3) Safety timeout — if /me-fetch hangs or rejects token, surface error.
  useEffect(() => {
    if (!tokensApplied || user) return;
    const t = setTimeout(() => {
      if (!user) {
        setError('Authenticatie mislukt. Token wordt niet geaccepteerd door de backend. Probeer opnieuw in te loggen.');
      }
    }, 8000);
    return () => clearTimeout(t);
  }, [tokensApplied, user, loading]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="max-w-md text-center space-y-3 px-4" data-testid="callback-error">
          <AlertTriangle className="w-8 h-8 text-red-500 mx-auto" />
          <h1 className="text-lg font-semibold text-[#1f2548]">Login mislukt</h1>
          <p className="text-sm text-slate-600">{error}</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="px-4 py-2 bg-[#1f2548] text-white rounded text-sm"
            data-testid="callback-retry"
          >
            Opnieuw inloggen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center space-y-2" data-testid="callback-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#1f2548] mx-auto" />
        <p className="text-sm text-slate-600">Sessie activeren...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
